import { Directive, ElementRef, HostListener, Input, OnInit, Renderer2 } from '@angular/core';
import { isNumber } from '../../models/utils';

@Directive({
    standalone: false,
    selector: '[resModalTools]'
})
export class ModalToolsDirective implements OnInit {
    modalContent: HTMLElement;
    modalHeader: HTMLElement;
    maximizeButton: HTMLElement;

    @Input() resizeHorizontal: boolean;
    @Input() resizeHorizontalMin: number;
    @Input() resizeHorizontalMax: number;

    @Input() resizeVertical: boolean;
    @Input() resizeVerticalMin: number;
    @Input() resizeVerticalMax: number;

    @Input() maximizable: boolean;
    @Input() modalDraggable: boolean;
    @Input() modalHasVariableHeight: boolean | number;
    @Input() maximizableAutoId: string;
    @Input() modalHasVariablePosition: boolean;

    private automationId = '';

    private maximized = false;
    private minimizeWidth: number;
    private minimizeHeight: number;
    private minimizeLeft: number;
    private minimizeTop: number;

    private mouseMoveEvent: MouseEvent;

    private resizers: Array<HTMLElement>;
    private dragger: HTMLElement;

    @HostListener('window:resize', ['$event'])
    onResize(event: any): void {
        if (this.maximized) {
            this.renderer.setStyle(this.modalContent, 'width', `${event.target.innerWidth - 20}px`);
            this.renderer.setStyle(this.modalContent, 'height', `${event.target.innerHeight - 20}px`);
        }
    }

    constructor(private renderer: Renderer2, private el: ElementRef) {
    }

    ngOnInit(): void {
        if (this.el.nativeElement.attributes.getNamedItem('data-automation-id')) {
            this.automationId = this.el.nativeElement.attributes.getNamedItem('data-automation-id').value;
            this.automationId = this.automationId.substring(0, this.automationId.indexOf('modal-container'));
        }
        this.getModalContent(this.el.nativeElement);
        if (this.modalHasVariableHeight && this.modalHasVariablePosition) {
            this.renderer.setStyle(this.modalContent, 'top', `-${Math.floor(window.innerHeight / 6)}px`);
        }
        if (this.modalContent) {
            if (this.resizeHorizontal || this.resizeVertical) {
                this.resizers = new Array<any>();
                if (this.resizeHorizontal) {
                    this.resizers.push(this.buildResizer('left'));
                    this.resizers.push(this.buildResizer('right'));
                }
                if (this.resizeVertical) {
                    this.resizers.push(this.buildResizer('top'));
                    this.resizers.push(this.buildResizer('bottom'));
                }
            }
            if (this.maximizable || this.modalDraggable) {
                this.getModalHeader(this.el.nativeElement);
                if (this.modalHeader) {
                    if (this.maximizable) {
                        this.buildMaximizeButton();
                    }
                    if (this.modalDraggable) {
                        this.dragger = this.buildDragger();
                    }
                }
            }
            if (this.modalHasVariableHeight) {
                setTimeout(() => {
                    const parent = this.renderer.parentNode(this.modalContent) as HTMLElement;
                    if (parent.classList && parent.classList.contains('modal-dialog')) {
                        let top = this.modalContent.offsetTop;
                        if (isNumber(this.modalHasVariableHeight)) {
                            top = (window.innerHeight - <number>this.modalHasVariableHeight) / 2;
                        }
                        this.renderer.setStyle(parent, 'align-items', 'flex-start');
                        this.renderer.setStyle(this.modalContent, 'top', `${top}px`);
                    }
                }, 200);
            }
        }
    }

    getModalContent(element: HTMLElement): void {
        if (!this.modalContent && element) {
            if (element.classList && element.classList.contains('modal-content')) {
                this.modalContent = element;
            } else {
                const parent = this.renderer.parentNode(element) as HTMLElement;
                this.getModalContent(parent);
            }
        }
    }

    getModalHeader(element: HTMLElement): void {
        if (!this.modalHeader && element) {
            if (element.classList && element.classList.contains('modal-header')) {
                this.modalHeader = element;
            } else {
                if (element.childNodes && element.childNodes.length > 0) {
                    Array.from(element.children).forEach(child => {
                        if (this.modalHeader) {
                            return;
                        }
                        this.getModalHeader(child as HTMLElement);
                    });
                }
            }
        }
    }

    buildResizer(resizerType: any): HTMLElement {
        const resizer = this.renderer.createElement('div');
        this.renderer.setAttribute(resizer, 'class', `modal-resizer-${resizerType}`);
        const automationId = `${this.automationId}modal-resizer-${resizerType}`;
        this.renderer.setAttribute(resizer, 'data-automation-id', automationId);
        this.renderer.appendChild(this.el.nativeElement, resizer);
        resizer.horizontal = resizerType === 'left' || resizerType === 'right';
        resizer.vertical = resizerType === 'top' || resizerType === 'bottom';

        resizer.mouseDownEvent = (e: MouseEvent) => {
            this.renderer.addClass(this.modalContent, 'no-select');
            document.addEventListener('mousemove', resizer.mouseMoveEvent);
            document.addEventListener('mouseup', resizer.mouseUpEvent);
            this.mouseMoveEvent = e;
        };

        resizer.mouseMoveEvent = (e: MouseEvent) => {
            if (this.mouseMoveEvent) {
                if (resizer.horizontal) {
                    let size = this.modalContent.clientWidth;
                    size += 2 * (resizerType === 'left' ? -1 : 1) * (e.screenX - this.mouseMoveEvent.screenX);
                    if (isNumber(this.resizeHorizontalMin)) {
                        size = Math.max(size, this.resizeHorizontalMin);
                    }
                    size = Math.max(size, 0);
                    if (isNumber(this.resizeHorizontalMax)) {
                        size = Math.min(size, this.resizeHorizontalMax);
                    }
                    size = Math.min(size, (window.innerWidth - 20));
                    this.renderer.setStyle(this.modalContent, 'width', `${size}px`);
                } else if (resizer.vertical) {
                    let size = this.modalContent.clientHeight;
                    size += 2 * (resizerType === 'top' ? -1 : 1) * (e.screenY - this.mouseMoveEvent.screenY);
                    if (isNumber(this.resizeVerticalMin)) {
                        size = Math.max(size, this.resizeVerticalMin);
                    }
                    size = Math.max(size, 0);
                    if (isNumber(this.resizeVerticalMax)) {
                        size = Math.min(size, this.resizeVerticalMax);
                    }
                    size = Math.min(size, (window.innerHeight - 20));
                    this.renderer.setStyle(this.modalContent, 'height', `${size}px`);
                }
                this.mouseMoveEvent = e;
            }
        };

        resizer.mouseUpEvent = () => {
            this.renderer.removeClass(this.modalContent, 'no-select');
            document.removeEventListener('mousemove', resizer.mouseMoveEvent);
            document.removeEventListener('mouseup', resizer.mouseUpEvent);
            this.mouseMoveEvent = null;
        };

        resizer.addEventListener('mousedown', resizer.mouseDownEvent);

        return resizer as HTMLElement;
    }

    buildMaximizeButton(): void {
        this.maximizeButton = this.renderer.createElement('button');
        this.renderer.setAttribute(this.maximizeButton, 'type', 'button');
        this.renderer.setAttribute(this.maximizeButton, 'aria-label', 'Maximize');
        const automationId = `${this.maximizableAutoId}-maximize-minimize-button`;
        this.renderer.setAttribute(this.maximizeButton, 'data-automation-id', automationId);

        const maxIcon = this.renderer.createElement('i');
        this.renderer.setAttribute(maxIcon, 'class', 'fa fa-window-maximize');
        this.renderer.appendChild(this.maximizeButton, maxIcon);

        this.renderer.insertBefore(this.modalHeader, this.maximizeButton, this.modalHeader.lastElementChild);

        this.maximizeButton.addEventListener('click', () => {
            if (this.maximized) {
                this.renderer.setStyle(this.modalContent, 'width', `${this.minimizeWidth}px`);
                this.renderer.setStyle(this.modalContent, 'height', `${this.minimizeHeight}px`);

                this.renderer.setStyle(this.modalContent, 'left', `${this.minimizeLeft}px`);
                this.renderer.setStyle(this.modalContent, 'top', `${this.minimizeTop}px`);

                this.renderer.removeClass(maxIcon, 'fa-window-restore');
                this.renderer.addClass(maxIcon, 'fa-window-maximize');

                if (this.resizers) {
                    this.resizers.forEach(resizer => {
                        this.renderer.removeClass(resizer, 'disabled');
                    });
                }
                if (this.dragger) {
                    this.renderer.removeClass(this.dragger, 'disabled');
                }
            } else {
                this.minimizeWidth = this.modalContent.clientWidth;
                this.minimizeHeight = this.modalContent.clientHeight;
                this.minimizeLeft = this.modalContent.style.left ? parseInt(this.modalContent.style.left as string, 10) : 0;
                this.minimizeTop = this.modalContent.style.top ? parseInt(this.modalContent.style.top as string, 10) : 0;

                this.renderer.setStyle(this.modalContent, 'width', `${window.innerWidth - 20}px`);
                this.renderer.setStyle(this.modalContent, 'height', `${window.innerHeight - 20}px`);

                this.renderer.setStyle(this.modalContent, 'left', `0px`);
                this.renderer.setStyle(this.modalContent, 'top', `0px`);

                this.renderer.removeClass(maxIcon, 'fa-window-maximize');
                this.renderer.addClass(maxIcon, 'fa-window-restore');

                if (this.resizers) {
                    this.resizers.forEach(resizer => {
                        this.renderer.addClass(resizer, 'disabled');
                    });
                }
                if (this.dragger) {
                    this.renderer.addClass(this.dragger, 'disabled');
                }
            }
            this.maximized = !this.maximized;
        });
    }

    buildDragger(): HTMLElement {
        const dragger = this.renderer.createElement('div');
        this.renderer.setAttribute(dragger, 'class', `modal-dragger`);
        const automationId = `${this.automationId}modal-dragger`;
        this.renderer.setAttribute(dragger, 'data-automation-id', automationId);
        if (this.modalHeader.children && this.modalHeader.children.length > 1) {
            this.renderer.setStyle(dragger, 'right', `${(this.modalHeader.children.length - 1) * 30}px`);
        }
        this.renderer.appendChild(this.el.nativeElement, dragger);

        dragger.mouseDownEvent = (e: MouseEvent) => {
            this.renderer.addClass(this.modalContent, 'no-select');
            document.addEventListener('mousemove', dragger.mouseMoveEvent);
            document.addEventListener('mouseup', dragger.mouseUpEvent);
            this.mouseMoveEvent = e;
        };

        dragger.mouseMoveEvent = (e: MouseEvent) => {
            if (this.mouseMoveEvent) {
                const movementX = e.screenX - this.mouseMoveEvent.screenX;
                const movementY = e.screenY - this.mouseMoveEvent.screenY;
                let left = this.modalContent.style.left ? parseInt(this.modalContent.style.left as string, 10) : 0;
                left += movementX;
                let top = this.modalContent.style.top ? parseInt(this.modalContent.style.top as string, 10) : 0;
                top += movementY;
                this.renderer.setStyle(this.modalContent, 'left', `${left}px`);
                this.renderer.setStyle(this.modalContent, 'top', `${top}px`);
                this.mouseMoveEvent = e;
            }
        };

        dragger.mouseUpEvent = () => {
            this.renderer.removeClass(this.modalContent, 'no-select');
            document.removeEventListener('mousemove', dragger.mouseMoveEvent);
            document.removeEventListener('mouseup', dragger.mouseUpEvent);
            this.mouseMoveEvent = null;
        };

        dragger.addEventListener('mousedown', dragger.mouseDownEvent, true);
        setTimeout(() => {
            if (this.modalHeader.children?.length > 0 && (this.modalHeader.firstChild as HTMLElement)?.title) {
                this.renderer.setAttribute(dragger, 'title', (this.modalHeader.firstChild as HTMLElement).title);
            }
        });

        return dragger as HTMLElement;
    }
}
